import { useTranslation } from "react-i18next";
import type { ProductPageConfig } from "./types";
import { ProductHero } from "./ProductHero";
import { ProductFeatureGroups } from "./ProductFeatureGroups";
import { ProductUseCases } from "./ProductUseCases";
import { ProductEnrichmentTable } from "./ProductEnrichmentTable";
import { ProductSteps } from "./ProductSteps";
import { ProductBenefits } from "./ProductBenefits";
import { ProductPricing } from "./ProductPricing";
import { ProductCTA } from "./ProductCTA";
import { PageSEO } from "@/components/seo/PageSEO";

export function ProductPageTemplate({
  productKey,
  icon,
  featureGroups,
  benefits,
  steps,
  useCases,
  enrichmentAttributes,
  accentClass = "bg-petrol-light",
  demoPlaceholder,
  additionalSections,
}: ProductPageConfig & { additionalSections?: { position: string; component: React.ReactNode }[] }) {
  const { t } = useTranslation();

  const productSchema = {
    "@type": "SoftwareApplication",
    name: t(`productPages.${productKey}.hero.title`),
    description: t(`productPages.${productKey}.overview`),
    applicationCategory: "BusinessApplication",
    operatingSystem: "Web",
    offers: {
      "@type": "Offer",
      availability: "https://schema.org/InStock",
      priceCurrency: "SEK",
    },
    provider: {
      "@type": "Organization",
      name: "Geposit",
      url: "https://geposit.se",
    },
  };

  return (
    <div>
      <PageSEO
        title={t(`productPages.${productKey}.hero.title`)}
        description={t(`seo.${productKey}`)}
        structuredData={productSchema}
      />
      <ProductHero productKey={productKey} icon={icon} accentClass={accentClass} />
      <ProductFeatureGroups productKey={productKey} featureGroups={featureGroups} accentClass={accentClass} />

      {useCases && useCases.length > 0 && (
        <ProductUseCases productKey={productKey} useCases={useCases} />
      )}

      {demoPlaceholder && (
        <section className="py-12 lg:py-28 bg-secondary/30">
          <div className="container mx-auto px-4 lg:px-8">
            {demoPlaceholder}
          </div>
        </section>
      )}

      {/* Additional sections: after-workflow */}
      {additionalSections?.filter(s => s.position === "after-workflow").map((s, i) => (
        <div key={`aw-${i}`}>{s.component}</div>
      ))}

      {enrichmentAttributes && enrichmentAttributes.length > 0 && (
        <ProductEnrichmentTable productKey={productKey} attributes={enrichmentAttributes} />
      )}

      {steps && steps.length > 0 && <ProductSteps steps={steps} />}

      {/* Additional sections: before-benefits */}
      {additionalSections?.filter(s => s.position === "before-benefits").map((s, i) => (
        <div key={`bb-${i}`}>{s.component}</div>
      ))}

      <ProductBenefits productKey={productKey} benefits={benefits} />

      <ProductPricing productKey={productKey} />

      <ProductCTA />
    </div>
  );
}
